import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Input,
  Table,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { APIgetReport } from '../../../api/Reports';

import AdminReportsDataListItem from './DataListItem';
import AdminReportsChartBar from './ChartBar';
import AdminReportsChartLine from './ChartLine';
import AdminReportsChartPie from './ChartPie';

import { slugify } from '../../../helpers';
import { reportPeriods } from '../../../helpers/Reports';

import LoadingSmall from '../../LoadingSmall';

const AdminReportsWidget = ({ widget }) => {
  const { intl } = useIntl();
  const [period, setPeriod] = useState('by_month');
  const [dateFrom, setDateFrom] = useState(null);
  const [data, setData] = useState(null);
  const [columns, setColumns] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const iconClass = widget.icon || 'fa-chart-simple';

  const fetchData = useCallback(() => {
    setDataLoading(true);
    return APIgetReport({
      report_id: widget.id,
      date_from: dateFrom.format('YYYY-MM-DD'),
      date_to: moment().format('YYYY-MM-DD'),
      period: widget.granularity ? period : undefined,
    }).then((response) => {
      setData(response.data);
      setColumns(response.columns);
      setDataLoading(false);
    });
  }, [widget, dateFrom]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateFrom) {
      fetchData();
    }
  }, [fetchData, dateFrom]);

  useEffect(() => {
    if (widget.defaultDateFrom) {
      setDateFrom(moment(widget.defaultDateFrom));
      return;
    }

    if (period === 'by_day') {
      if (widget.period) {
        setDateFrom(moment().subtract(1, 'days'));
      } else {
        setDateFrom(moment().subtract(14, 'days'));
      }
    }

    if (period === 'by_week') {
      if (widget.period) {
        setDateFrom(moment().subtract(1, 'weeks'));
      } else {
        setDateFrom(moment().subtract(6, 'weeks'));
      }
    }

    if (period === 'by_month') {
      if (widget.period) {
        setDateFrom(moment().subtract(1, 'months'));
      } else {
        setDateFrom(moment().subtract(6, 'months'));
      }
    }

    if (period === 'by_year') {
      if (widget.period) {
        setDateFrom(moment().subtract(1, 'years'));
      } else {
        setDateFrom(moment().subtract(6, 'years'));
      }
    }
  }, [period, widget]);

  const handleChangePeriod = (e) => {
    const { value } = e.target;
    setPeriod(value);
  };

  return (
    <div className={`report-widget report-widget-${widget.id}`}>
      <Card>
        <CardHeader>
          <div>
            <Link to={`/admin/reports/${widget.id}`}>
              <i className={`far fa-2x ${iconClass}`} />
              <h5>{widget.name}</h5>
            </Link>
          </div>
          {(widget.granularity || widget.period) && (
            <div>
              <Input
                type="select"
                className="period-select custom-select"
                bsSize="sm"
                onChange={handleChangePeriod}
                defaultValue={period}
              >
                {reportPeriods(intl).map(rPeriod => (
                  <option key={rPeriod.id} value={rPeriod.id}>
                    {widget.granularity ? rPeriod.granularityText : rPeriod.periodText}
                  </option>
                ))}
              </Input>
            </div>
          )}
        </CardHeader>
        <CardBody>
          {dataLoading ? (
            <LoadingSmall />
          ) : (
            <>
              {widget.type === 'table' && (
                <Table className="report-widget-table" responsive>
                  <thead>
                    <tr>
                      {Object.keys(columns).map(key => (
                        <th key={key}>{columns[key]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.slice(0, 5).map(item => (
                      <AdminReportsDataListItem
                        key={
                          item.id ||
                          item.uuid ||
                          item.file_id ||
                          slugify(item.term)
                        }
                        columns={columns}
                        item={item}
                        inWidget={true}
                      />
                    ))}
                  </tbody>
                </Table>
              )}
              {widget.type === 'chart' && (
                <div className="report-widget-chart">
                  {widget.chartType === 'bar' && (
                    <AdminReportsChartBar
                      report={widget}
                      data={data}
                    />
                  )}
                  {widget.chartType === 'line' && (
                    <AdminReportsChartLine
                      report={widget}
                      data={data}
                    />
                  )}
                  {widget.chartType === 'pie' && (
                    <AdminReportsChartPie
                      report={widget}
                      data={data}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

AdminReportsWidget.propTypes = {
  widget: PropTypes.object.isRequired,
};

export default AdminReportsWidget;
